import React from 'react';
import Icon from '../../../components/AppIcon';

const ProgressPanel = ({ userStats }) => {
  const achievements = [
    {
      id: 1,
      title: "First Steps",
      description: "Complete your first mission",
      icon: "Award",
      unlocked: userStats?.missionsCompleted > 0,
      progress: Math.min(userStats?.missionsCompleted, 1)
    },
    {
      id: 2,
      title: "Element Master",
      description: "Master element selection techniques",
      icon: "Target",
      unlocked: userStats?.skillLevels?.elementSelection >= 3,
      progress: userStats?.skillLevels?.elementSelection / 5
    },
    {
      id: 3,
      title: "Speed Runner",
      description: "Complete 5 missions in a day",
      icon: "Zap",
      unlocked: userStats?.dailyStreak >= 5,
      progress: Math.min(userStats?.dailyStreak / 5, 1)
    },
    {
      id: 4,
      title: "Boss Slayer",
      description: "Defeat your first boss mission",
      icon: "Crown",
      unlocked: userStats?.bossesDefeated > 0,
      progress: Math.min(userStats?.bossesDefeated, 1)
    }
  ];

  const skillAreas = [
    {
      name: "Element Selection",
      level: userStats?.skillLevels?.elementSelection,
      maxLevel: 5,
      color: "bg-success"
    },
    {
      name: "Waits & Timing",
      level: userStats?.skillLevels?.waits,
      maxLevel: 5,
      color: "bg-primary"
    },
    {
      name: "Advanced Actions",
      level: userStats?.skillLevels?.actions,
      maxLevel: 5,
      color: "bg-warning"
    },
    {
      name: "Page Objects",
      level: userStats?.skillLevels?.pageObjects,
      maxLevel: 5,
      color: "bg-secondary"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <div className="bg-card border border-subtle rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="TrendingUp" size={20} className="mr-2" />
          Learning Progress
        </h3>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">{userStats?.missionsCompleted}</div>
            <div className="text-sm text-muted-foreground">Missions Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">{userStats?.currentStreak}</div>
            <div className="text-sm text-muted-foreground">Day Streak</div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground">Level {userStats?.level}</span>
            <span className="text-muted-foreground">{userStats?.xp}/{userStats?.nextLevelXp} XP</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${(userStats?.xp / userStats?.nextLevelXp) * 100}%` }}
            />
          </div>
        </div>
      </div>
      {/* Skill Levels */}
      <div className="bg-card border border-subtle rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Brain" size={20} className="mr-2" />
          Skill Levels
        </h3>
        
        <div className="space-y-4">
          {skillAreas?.map((skill) => (
            <div key={skill?.name}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">{skill?.name}</span>
                <span className="text-sm text-muted-foreground">
                  {skill?.level}/{skill?.maxLevel}
                </span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full ${skill?.color} transition-all duration-500`}
                  style={{ width: `${(skill?.level / skill?.maxLevel) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Achievements */}
      <div className="bg-card border border-subtle rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Trophy" size={20} className="mr-2" />
          Achievements
        </h3>
        
        <div className="space-y-3">
          {achievements?.map((achievement) => (
            <div
              key={achievement?.id}
              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                achievement?.unlocked 
                  ? 'bg-success/10 border border-success/20' :'bg-muted/30 opacity-60'
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                achievement?.unlocked ? 'bg-success text-white' : 'bg-muted text-muted-foreground'
              }`}>
                <Icon name={achievement?.icon} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-foreground">{achievement?.title}</h4>
                <p className="text-xs text-muted-foreground">{achievement?.description}</p>
                {!achievement?.unlocked && achievement?.progress > 0 && (
                  <div className="mt-1 w-full h-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${achievement?.progress * 100}%` }}
                    />
                  </div>
                )}
              </div>
              {achievement?.unlocked && (
                <Icon name="Check" size={16} color="var(--color-success)" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressPanel;